import { Dialog } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Spinner } from "@via/components/loading/loading";
import { useEffect, useMemo, type JSX } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import FormInputComponent from "@via/components/inputs/form-input-component";
import {
  addUserGroupMemberOptions,
  requiredValidation,
} from "@via/utils/form-validators";
import { useEditPremiumDataset } from "@via/pages/data/premium-dataset/premium-dataset.api.tsx";
import SelectComponent from "@via/components/select/select";
import UserGroupSelectComponent from "@via/components/select/user-group-select-component";
import {
  UpdateVmetaDataSetRequestBody,
  VmetaDataSetRequestParams,
  VmetaDataSetResponse,
} from "common";
import {
  deduplicatePermissions,
  filterPermissions,
  transformPermissions,
} from "@via/pages/data/premium-dataset/utils/helper";
import { useAuth } from "@via/hooks/useAuth";
import {
  PREMIUM_DATASET_PERMISSION_OPTIONS,
  USER_ROLES,
} from "@via/utils/constants";

const everyoneOption = { value: "everyone", label: "Everyone" };

const defaultPermission = {
  type: "user",
  email: "",
  read: true,
  write: false,
};

export default function EditPremiumDatasetForm({
  setOpen,
  dataset,
}: {
  setOpen: (open: boolean) => void;
  dataset: VmetaDataSetResponse | null;
}): JSX.Element {
  const { user } = useAuth();
  const handleClose = () => setOpen(false);

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<{
    name: string;
    perms: any;
  }>({
    mode: "onSubmit",
    defaultValues: {
      name: dataset?.name || "",
      perms: dataset?.perms || [],
    },
  });

  const permissionOptions = useMemo(() => {
    if (user && user?.userRole === USER_ROLES.ADMIN) {
      return [everyoneOption, ...PREMIUM_DATASET_PERMISSION_OPTIONS];
    } else {
      return PREMIUM_DATASET_PERMISSION_OPTIONS;
    }
  }, [user]);

  const missingIds = useMemo(() => {
    return (
      dataset?.perms
        ?.filter(perm => perm.type === "group")
        .map(perm => perm.groupId) || []
    );
  }, [dataset]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "perms",
  });

  const {
    mutateAsync: editPremiumDataset,
    isPending: isEditPremiumDatasetLoading,
    isSuccess: isEditPremiumDatasetSuccess,
  } = useEditPremiumDataset();

  const handleEditDataset = async payload => {
    const transformedPerms = transformPermissions(payload.perms);
    const filteredPerms = filterPermissions(transformedPerms);
    const uniquePerms = deduplicatePermissions(filteredPerms);

    const body = {
      id: dataset?._id || "",
      name: payload.name,
      perms: uniquePerms,
    };

    await editPremiumDataset(
      body as VmetaDataSetRequestParams & UpdateVmetaDataSetRequestBody
    );
  };

  useEffect(() => {
    isEditPremiumDatasetSuccess && handleClose();
  }, [isEditPremiumDatasetSuccess]);

  return (
    <form
      className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl"
      onSubmit={e => {
        e.preventDefault();
        void handleSubmit(handleEditDataset)(e);
      }}>
      <div className="h-0 flex-1 overflow-y-auto">
        <div className="border-b bg-gray-50 px-4 py-6 sm:px-6">
          <div className="flex items-center justify-between">
            <Dialog.Title className="text-base font-semibold leading-6 text-gray-900">
              Dataset
            </Dialog.Title>
            <div className="ml-3 flex h-7 items-center">
              <button
                type="button"
                className="relative rounded-md text-gray-900 duration-150 ease-in-out hover:text-gray-600 focus:outline-none"
                onClick={handleClose}>
                <span className="absolute -inset-2.5" />
                <span className="sr-only">Close panel</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
          </div>
          <div className="mt-1">
            <p className="text-sm text-gray-500">Update the dataset details.</p>
          </div>
        </div>
        <div className="flex flex-1 flex-col justify-between">
          <div className="divide-y divide-gray-200 px-2 sm:px-4">
            <div className="space-y-6 pb-5 pt-6">
              <FormInputComponent
                control={control}
                name="name"
                label="Name"
                placeholder="Enter Name"
                validations={requiredValidation("Name")}
              />
            </div>
          </div>
          <div className="flex justify-between text-center  px-2 sm:px-4">
            <label className=" flex items-center text-sm font-medium leading-6 text-gray-900">
              Permissions
            </label>
            <button
              type="button"
              onClick={() => append(defaultPermission)}
              className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
              + Add Permission
            </button>
          </div>
          <div className="flex flex-col px-2 sm:px-4 max-h-[500px] overflow-auto">
            {fields.map((field, idx) => {
              const type = watch(`perms.${idx}.type`);
              const isEveryone = type === "everyone";
              if (user?.userRole === USER_ROLES.USER && type === "everyone") {
                return null;
              }
              return (
                <div
                  key={field.id}
                  className="flex items-center gap-2 border-b py-2">
                  <div className="min-w-[150px]">
                    <SelectComponent
                      control={control}
                      name={`perms.${idx}.type`}
                      options={permissionOptions}
                      placeholder="Select type"
                      enableClear={false}
                    />
                  </div>
                  {type === "user" && (
                    <div className="-mt-2 w-full">
                      <FormInputComponent
                        control={control}
                        name={`perms.${idx}.email`}
                        placeholder="Enter email"
                        validations={addUserGroupMemberOptions.email}
                      />
                    </div>
                  )}
                  {type === "group" && (
                    <div className="w-full">
                      <UserGroupSelectComponent
                        control={control}
                        name={`perms.${idx}.groupId`}
                        missingId={missingIds}
                        rules={requiredValidation("User Group")}
                        menuPlacement="top"
                        errors={errors}
                        uniqueIdentifier="vmetaGroupId"
                        disableIfNotFound={true}
                      />
                    </div>
                  )}
                  <label className="flex items-center gap-1">
                    <FormInputComponent
                      control={control}
                      name={`perms.${idx}.read`}
                      type="checkbox"
                      className="h-6 w-6 -mt-2"
                    />
                    Read
                  </label>
                  {!isEveryone && (
                    <label className="flex items-center gap-1">
                      <FormInputComponent
                        control={control}
                        name={`perms.${idx}.write`}
                        type="checkbox"
                        className="h-6 w-6 mr-2 -mt-2"
                      />
                      Write
                    </label>
                  )}
                  <button
                    type="button"
                    onClick={() => remove(idx)}
                    className="px-2">
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div className="flex flex-shrink-0 justify-end px-4 py-4">
        <button
          id="edit-dataset-cancel"
          type="button"
          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          onClick={handleClose}>
          Cancel
        </button>
        <button
          id="edit-dataset-save"
          type="submit"
          className="ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
          <Spinner loading={isEditPremiumDatasetLoading}>Save</Spinner>
        </button>
      </div>
    </form>
  );
}
